import { Controller } from '@hotwired/stimulus'
import { i18n } from '../lib/i18n'

/**
 * Subscription Quantity Controller
 * 
 * Updates the price display when the quantity input changes
 */
interface Tier {
  up_to: number | null
  unit_amount: number | null
  flat_amount: number | null
}

export default class extends Controller {
  static targets = ['basePrice', 'discount', 'totalPrice', 'discountRow']
  static values = {
    baseSeatCount: { type: Number, default: 2 },
    discountPerSeat: { type: Number, default: 25.0 },
    monthlyTiers: { type: Array, default: [] }
  }

  declare basePriceTarget: HTMLElement
  declare discountTarget: HTMLElement
  declare totalPriceTarget: HTMLElement
  declare discountRowTarget: HTMLElement
  declare baseSeatCountValue: number
  declare discountPerSeatValue: number
  declare monthlyTiersValue: Tier[]
  updatePrice(event: Event) {
    const input = event.target as HTMLInputElement
    const quantity = parseInt(input.value, 10) || 2

    // Calculate base price using tiered pricing from Stripe
    const basePrice = this.calculateTieredTotal(quantity, this.monthlyTiersValue)

    // Calculate discount for seats above base_seat_count
    const discount = quantity > this.baseSeatCountValue 
      ? (quantity - this.baseSeatCountValue) * this.discountPerSeatValue 
      : 0

    const totalPrice = Math.max(0, basePrice - discount)

    // Update display
    this.basePriceTarget.textContent = i18n.t('subscription_quantity.price', { amount: basePrice.toFixed(2) })
    this.discountTarget.textContent = i18n.t('subscription_quantity.discount', { amount: discount.toFixed(2) })
    this.totalPriceTarget.textContent = i18n.t('subscription_quantity.price', { amount: totalPrice.toFixed(2) })

    // Show/hide discount row
    if (discount > 0) {
      this.discountRowTarget.style.display = 'flex'
    } else {
      this.discountRowTarget.style.display = 'none'
    }
  }

  private calculateTieredTotal(quantity: number, tiers: Tier[]): number {
    if (!tiers || tiers.length === 0) {
      // Fallback: if no tiers, assume $50 per seat (shouldn't happen in production)
      return quantity * 50.0
    }

    let totalCents = 0
    let remainingQuantity = quantity

    for (const tier of tiers) {
      if (remainingQuantity <= 0) break

      const upTo = tier.up_to === null ? Infinity : tier.up_to
      const unitsInTier = Math.min(remainingQuantity, upTo)

      if (tier.unit_amount) {
        totalCents += unitsInTier * tier.unit_amount
      }
      if (tier.flat_amount) {
        totalCents += tier.flat_amount
      }

      remainingQuantity -= unitsInTier
    }

    return totalCents / 100.0
  }
}

